import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ITrainingBlock } from '../../models/allenamenti';
import { toIsoDate } from './allenamenti.constants';

export type AllenamentiView = 'settimana' | 'analisi';

/**
 * Squadra della pagina: prop (incorporata nel dettaglio squadra) o URL (route
 * a sé) — stessa regola di fallback usata sia da `useAllenamentiPage` sia dal
 * guscio esterno `AllenamentiPage` (che ne ha bisogno per resettare gli
 * appunti dei blocchi al cambio squadra, F1: vedi BlockClipboardProvider).
 * Estratta qui perché è l'unica fonte di verità su "qual è la squadra",
 * niente due formule uguali scritte in due file.
 */
export const useResolvedTeamId = (teamIdProp?: string): string => {
  const params = useParams<{ teamId?: string }>();
  return teamIdProp ?? params.teamId ?? '';
};

/**
 * Stato della pagina: quale squadra, quale vista (settimana/analisi) e quale
 * blocco è aperto nella modal.
 *
 * `teamId` può arrivare come prop (quando la pagina è incorporata nel dettaglio
 * squadra) o dall'URL (quando è una route a sé).
 *
 * La vista si persiste in `?view=` con lo stesso pattern già usato da
 * TeamDetail per `?tab=` (setSearchParams con replace, per non inquinare la
 * history): un deep-link diretto all'Analisi funziona così senza bisogno di
 * una route dedicata (design M2/M3 §5.1 — niente terza pagina orfana).
 */
export const useAllenamentiPage = (teamIdProp?: string) => {
  const teamId = useResolvedTeamId(teamIdProp);

  const [searchParams, setSearchParams] = useSearchParams();
  const view: AllenamentiView = searchParams.get('view') === 'analisi' ? 'analisi' : 'settimana';

  const setView = (next: AllenamentiView) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === 'settimana') nextParams.delete('view');
        else nextParams.set('view', next);
        return nextParams;
      },
      { replace: true },
    );
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<ITrainingBlock | null>(null);
  const [modalDate, setModalDate] = useState<string>(() => toIsoDate(new Date()));

  const openCreate = (iso: string) => {
    setEditingBlock(null);
    setModalDate(iso);
    setIsModalOpen(true);
  };

  const openEdit = (block: ITrainingBlock) => {
    setEditingBlock(block);
    setModalDate(block.localDate);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingBlock(null);
  };

  return {
    teamId,
    view,
    setView,
    isModalOpen,
    editingBlock,
    modalDate,
    openCreate,
    openEdit,
    closeModal,
  };
};
